import { createContext, useContext, useEffect, useState } from "react";

/**
 * Breadcrumb trail context.
 *
 * Detail pages publish a custom hierarchical trail (e.g. `Clients › Acme Corp`) ending in
 * the real entity name, with clickable parent links. When no page publishes a trail, the
 * <Breadcrumbs/> component falls back to deriving one from the URL path.
 *
 * A trail item is `{ label, to? }`. Omit `to` (or it being last) renders it as the current
 * page. "Home" is always prepended by the renderer.
 */
const BreadcrumbCtx = createContext(null);

export function BreadcrumbProvider({ children }) {
  const [trail, setTrail] = useState(null);
  return <BreadcrumbCtx.Provider value={{ trail, setTrail }}>{children}</BreadcrumbCtx.Provider>;
}

export function useBreadcrumbTrail() {
  return useContext(BreadcrumbCtx)?.trail ?? null;
}

/**
 * Publish a custom breadcrumb trail for the current page. Clears on unmount so the next
 * page falls back to its own trail or the path-derived default.
 *
 * The trail is only published once the final (current-page) crumb has a resolved label —
 * this avoids flashing an empty crumb while the entity name is still loading.
 *
 * @param {Array<{label: string, to?: string}>} items
 * @param {Array<any>} deps  re-publish when these change (typically [entityName])
 */
export function useSetBreadcrumb(items, deps = []) {
  const ctx = useContext(BreadcrumbCtx);
  useEffect(() => {
    if (!ctx) return undefined;
    const clean = (items || []).filter((i) => i && i.label);
    const ready = clean.length && items?.[items.length - 1]?.label;
    ctx.setTrail(ready ? clean : null);
    return () => ctx.setTrail(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
